import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, Text, StyleSheet, Pressable,
} from 'react-native';
import { shallowEqual, useSelector } from 'react-redux';
import Colors from '../../../../constants/Colors';
import isMobile from '../../../../components/IsMobile';
import updateTicketPriority from '../../../../requests/Tickets/UpdateTicketPriority';
import SelectionModal from '../../../../components/SelectionModal';
import getPriorityColor from '../../../../components/PriorityColorScheme';
import Style from '../../../../constants/Style';
import PressableStyled, { PressableState } from '../../../../components/PressableStyled';
import DropdownIcon12 from '../../../../components/12pxIcons/DropdownIcon12';
import TickIcon16 from '../../../../components/16pxIcons/TickIcon16';
import { Ticket, TicketPriority, UserPermission } from '../../../../constants/Types';
import { Building } from '../../../LocationManagement/components/AddBuildingModal';
import { BuildingSettingsPriority } from '../../../LocationManagement/components/BuildingSettingsModal/data';

interface TicketPrioritySelectorRowProps {
  id: string,
  priority: TicketPriority,
  showPriorityDropdown: boolean,
  setShowPriorityDropdown: Dispatch<SetStateAction<boolean>>,
  setLoading: Dispatch<SetStateAction<boolean>>,
  setShowSpinner: Dispatch<SetStateAction<boolean>>,
  updateSelectedTicketInList,
}

const TicketPrioritySelectorRow = ({
  id, priority, showPriorityDropdown, setShowPriorityDropdown, setLoading, setShowSpinner,
  updateSelectedTicketInList,
}: TicketPrioritySelectorRowProps) => {
  const [selectedPriorityIndex, setSelectedPriorityIndex] = useState<number>(priority ?? 0);
  const [priorityChangeLoading, setPriorityChangeLoading] = useState<boolean>(false);
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canUpdateTickets: boolean = isAdmin || permissions.includes(UserPermission.TICKETS_UPDATE);
  const slaNames: string[] = useSelector((state: {
    selectedBuilding: Building
  }) => state.selectedBuilding
    .SlaHours?.map((item: BuildingSettingsPriority) => item.Name), shallowEqual);

  useEffect(() => {
    setSelectedPriorityIndex(priority ?? 0);
  }, [id, priority]);

  const apply = (priorityIndex: number) => {
    setShowSpinner(true);
    setPriorityChangeLoading(true);
    setShowPriorityDropdown(false);
    updateTicketPriority(id, priorityIndex).then((res: Ticket) => {
      updateSelectedTicketInList(res);
    }).finally(() => {
      setLoading(true);
      setPriorityChangeLoading(false);
    });
  };

  const PriorityList = () => <>
    {slaNames.map((slaName: string, index: number) => {
      const disabled: boolean = index === selectedPriorityIndex;
      return <Pressable
        key={index}
        {...{ disabled }}
        style={(state: PressableState) => [
          Style.row,
          styles.item,
          state.hovered && Style.listItemHovered,
          state.pressed && Style.listItemPressed,
        ]}
        onPress={() => {
          setSelectedPriorityIndex(index);
          if (!isMobile) apply(index);
        }}
      >
        <View style={styles.itemContent}>
          <View style={[styles.dot, { backgroundColor: getPriorityColor(index) }]} />
          <Text style={styles.name}>{`P${index + 1} - ${slaName}`}</Text>
        </View>
        {disabled && <TickIcon16 />}
      </Pressable>;
    })}
  </>;

  return <>
    <PressableStyled
      style={styles.trigger}
      onPress={() => setShowPriorityDropdown(!showPriorityDropdown)}
      disabled={!canUpdateTickets}
      maintainOpacityWhenDisabled
    >
      <View style={styles.triggerContent}>
        <View style={[styles.dot, { backgroundColor: getPriorityColor(priority ?? 0) }]} />
        <Text style={styles.triggerText}>{slaNames[priority] ?? `P${(priority ?? 0) + 1}`}</Text>
      </View>
      {canUpdateTickets && <DropdownIcon12 color={Colors.light.hereworksBlack600} />}
    </PressableStyled>
    {isMobile ? <SelectionModal
      title='Change priority'
      listContent={<PriorityList />}
      isVisible={showPriorityDropdown}
      hide={() => {
        setShowPriorityDropdown(false);
        setSelectedPriorityIndex(priority ?? 0);
      }}
      apply={() => apply(selectedPriorityIndex)}
      loading={priorityChangeLoading}
    /> : (showPriorityDropdown && <View style={styles.dropdown}>
      <View style={styles.dropdownTip} />
      <PriorityList />
    </View>)}
  </>;
};

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: Colors.light.hereworksWhite600,
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    backgroundColor: Colors.light.plainBackground,
  },
  triggerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 6,
  },
  triggerText: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    color: Colors.light.hereworksBlack600,
  },
  dropdown: {
    position: 'absolute',
    top: 36,
    start: 0,
    zIndex: 1000,
    elevation: 20,
    backgroundColor: Colors.light.plainBackground,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    borderRadius: 8,
    minWidth: 190,
    maxHeight: 260,
    overflow: 'hidden',
  },
  dropdownTip: {
    backgroundColor: Colors.light.hereworksBlue100,
    width: 12,
    height: 12,
    borderRadius: 4,
    position: 'absolute',
    top: -6,
    start: 12,
    transform: [{ rotateZ: '45deg' }],
  },
  item: {
    padding: 10,
    borderRadius: 4,
    backgroundColor: Colors.light.plainBackground,
    justifyContent: 'space-between',
  },
  itemContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  name: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 14,
    lineHeight: 18,
    marginHorizontal: 8,
    color: Colors.light.hereworksBlack600,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginRight: 8,
  },
});

export default TicketPrioritySelectorRow;
